import React, { useState } from "react";
import { Trans } from "@lingui/react/macro";
import { useLanguageStore } from "../../stores/languageStore.ts";
import {
  BookingChangeRequestResponseDto,
  BookingChangeRequestStatus,
  useBookingChangeRequestsQuery,
} from "../../api/bookingChangeRequestApi.ts";
import { format } from "date-fns";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  History,
  MessageSquare,
  RotateCcw,
  X,
  XCircle,
} from "lucide-react";

interface ChangeRequestHistoryProps {
  bookingId: string;
}

const statusConfig: Record<
  BookingChangeRequestStatus,
  {
    icon: React.FC<{ className?: string }>;
    badgeClasses: string;
    label: string;
  }
> = {
  ACCEPTED: {
    icon: Check,
    badgeClasses: "bg-green-100 text-green-800 border-green-300",
    label: "Accepted",
  },
  REJECTED: {
    icon: X,
    badgeClasses: "bg-red-100 text-red-800 border-red-300",
    label: "Rejected",
  },
  PENDING: {
    icon: Clock,
    badgeClasses: "bg-yellow-100 text-yellow-800 border-yellow-300",
    label: "Pending",
  },
  SUPERSEDED: {
    icon: RotateCcw,
    badgeClasses: "bg-gray-100 text-gray-600 border-gray-300",
    label: "Superseded",
  },
  CANCELLED: {
    icon: XCircle,
    badgeClasses: "bg-gray-100 text-gray-600 border-gray-300",
    label: "Cancelled",
  },
};

const TimelineEntry: React.FC<{
  changeRequest: BookingChangeRequestResponseDto;
  isLast: boolean;
}> = ({ changeRequest, isLast }) => {
  const { dateFnsLocale } = useLanguageStore();

  const config = statusConfig[changeRequest.status];
  const StatusIcon = config.icon;

  const formatTimeslot = (from: string, to: string): string => {
    const fromDate = new Date(from);
    const toDate = new Date(to);
    return `${format(fromDate, "EEE MMM d, HH:mm", { locale: dateFnsLocale })} - ${format(toDate, "HH:mm", { locale: dateFnsLocale })}`;
  };

  const formatDateTime = (dateStr: string): string => {
    return format(new Date(dateStr), "PPP p", { locale: dateFnsLocale });
  };

  return (
    <div className="relative flex gap-3">
      {/* Timeline line and dot */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border ${config.badgeClasses}`}
        >
          <StatusIcon className="h-3.5 w-3.5" />
        </div>
        {!isLast && <div className="w-px grow bg-gray-200" />}
      </div>

      {/* Content */}
      <div className={`${isLast ? "pb-0" : "pb-5"} min-w-0 flex-1`}>
        {/* Header: status badge + date */}
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium ${config.badgeClasses}`}
          >
            {config.label}
          </span>
          <span className="text-xs text-gray-500">
            {formatDateTime(changeRequest.createdAt)}
          </span>
        </div>

        {/* Requested by */}
        <p className="mb-1.5 text-sm text-gray-700">
          <Trans>Requested by {changeRequest.requestedByName}</Trans>
        </p>

        {/* Timeslot changes */}
        <div className="mb-2 space-y-1">
          {changeRequest.timeslotChanges.map((change, index) => (
            <div
              key={index}
              className="flex flex-col gap-0.5 rounded bg-gray-50 px-2.5 py-1.5 text-xs sm:flex-row sm:items-center sm:gap-2"
            >
              <span className="text-gray-600">
                {formatTimeslot(change.originalFrom, change.originalTo)}
              </span>
              <ArrowRight className="hidden h-3 w-3 shrink-0 text-gray-400 sm:block" />
              <span className="font-medium text-gray-800">
                {formatTimeslot(change.proposedFrom, change.proposedTo)}
              </span>
            </div>
          ))}
        </div>

        {/* Message */}
        {changeRequest.message && (
          <div className="mb-1.5 flex items-start gap-1.5 text-sm text-gray-600">
            <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="italic">{changeRequest.message}</span>
          </div>
        )}

        {/* Response message (for rejected requests) */}
        {changeRequest.responseMessage && (
          <div className="mb-1.5 text-sm text-gray-600">
            <span className="font-medium">
              <Trans>Response:</Trans>
            </span>{" "}
            {changeRequest.responseMessage}
          </div>
        )}

        {/* Responded at */}
        {changeRequest.respondedAt && (
          <p className="text-xs text-gray-400">
            <Trans>
              Responded on {formatDateTime(changeRequest.respondedAt)}
            </Trans>
          </p>
        )}
      </div>
    </div>
  );
};

const ChangeRequestHistory: React.FC<ChangeRequestHistoryProps> = ({
  bookingId,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: changeRequests, isLoading } =
    useBookingChangeRequestsQuery(bookingId);

  const sortedRequests = React.useMemo(() => {
    if (!changeRequests) return [];
    return [...changeRequests].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [changeRequests]);

  // Don't show the toggle button while loading
  if (isLoading) {
    return null;
  }

  return (
    <div>
      {/* Toggle button */}
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800"
      >
        <History className="h-4 w-4" />
        {isOpen ? <Trans>Hide History</Trans> : <Trans>Show History</Trans>}
        {isOpen ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        {sortedRequests.length > 0 && (
          <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-xs text-gray-600">
            {sortedRequests.length}
          </span>
        )}
      </button>

      {/* Timeline content */}
      {isOpen && (
        <div className="mt-3">
          {sortedRequests.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">
              <Trans>No changes have been requested for this booking.</Trans>
            </p>
          ) : (
            <div className="pl-1">
              {sortedRequests.map((cr, index) => (
                <TimelineEntry
                  key={cr.id}
                  changeRequest={cr}
                  isLast={index === sortedRequests.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangeRequestHistory;
